import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserMinus } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { initials } from 'components/rating/RatingForm';
import { apiErrorMessage } from 'utils/errorMessage';
import type { FinancialOperationView, InterventionRequest, ReminderSettings } from 'api/interventionRequestsApi';
import { parisDateTime } from 'utils/interventionDate';
import { activeLocale } from 'i18n';
import { moneyText } from 'utils/money';
import { statusDot, statusTone } from 'pages/AdminInterventionRequests/vocabulary';
import RemindButton from './RemindButton';
import SecondaryZonePill from './SecondaryZonePill';
import TravelFeeEditor from './TravelFeeEditor';
import { remindHint, remindState, rowPhrase, rowStatusLabel } from './solicitationRows';
import { reminderNote, technicianName } from './upcomingAssignment';

/**
 * Who was already solicited on this mission, what each one answered, and the gestures the panel
 * offers on them — remind or withdraw one pending request (L6, E6), or release the mission.
 *
 * The follow-up's `MissionSolicitations` reads its rows through `solicitationRows` and offers
 * « Relancer » only; withdrawing and releasing stay here, on the ticket screen (D36).
 */

interface SolicitedTechniciansProps {
  /** Every live request of the mission, newest first. Cancelled ones are filtered by the caller. */
  requests: InterventionRequest[];
  /** The request that was accepted, if any — it alone makes a release possible (R-072). */
  acceptor: InterventionRequest | null;
  /** Still awaiting an answer: with none and no acceptor, there is nothing left to release. */
  pendingCount: number;
  /** « 3 sollicités · 1 accepté · 2 dépassées », built by the caller from the same list. */
  recap: string;
  cancelError: unknown;
  /** The request whose reminder is in flight — its button spins and every « Relancer » waits. */
  remindingId: number | null;
  remindError: unknown;
  /** The desk saves the deadline from here (its workflow is on) — decides the deadline-passed hint. */
  deadlineEditable: boolean;
  onCancel: (request: InterventionRequest) => void;
  onRemind: (request: InterventionRequest) => void;
  onRelease: () => void;
  /** The mission's live travel fees (F19), one per technician at most. */
  fees: FinancialOperationView[];
  /** The backend's reminder settings; absent until the mission read lands. */
  reminderSettings?: ReminderSettings;
}

export const SolicitedTechnicians = ({
  requests,
  acceptor,
  pendingCount,
  recap,
  cancelError,
  remindingId,
  remindError,
  deadlineEditable,
  onCancel,
  onRemind,
  onRelease,
  fees,
  reminderSettings,
}: SolicitedTechniciansProps) => {
  const { t } = useTranslation();
  const [editingFeeOf, setEditingFeeOf] = useState<number | null>(null);

  if (requests.length === 0) return null;
  const now = Date.now();
  const listed = [...requests].reverse().filter((request) => request.status !== 'accepted');
  /** F16/F19 — « Frais » stays editable on a pending row and on the acceptor's. */
  const feeControl = (request: InterventionRequest) => {
    const fee = fees.find((candidate) => candidate.technicianUserId === request.technician.id) ?? null;
    if (!fee && request.zone !== 'secondary') return null; // R3-G: offer "Add fees" only off a secondary zone
    return editingFeeOf === request.id ? (
      <TravelFeeEditor request={request} fee={fee} onDone={() => setEditingFeeOf(null)} />
    ) : (
      <button
        type="button"
        className="mt-1 min-h-[28px] text-left text-[12px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        onClick={() => setEditingFeeOf(request.id)}
      >
        {fee ? t('Fees: {{amount}} · Edit', { amount: moneyText(fee, activeLocale()) }) : t('Add fees')}
      </button>
    );
  };

  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-[.05em] text-muted-foreground/70">
          {t('Solicited technicians')}
        </span>
        <span className="text-[11.5px] text-muted-foreground/70">{recap}</span>
      </div>
      <ul className="flex flex-col gap-[7px]">
        {/* Chronological, oldest first: grouping by status would hide the order in which the
            mission actually went round, which is what the reader came for. */}
        {listed.map((request) => {
          const remind = request.status === 'pending' ? remindState(request, now) : null;
          const hint = remindHint(t, request, remind, true, deadlineEditable);
          const auto = request.status === 'pending' && request.reminderCount > 0 && request.lastReminderSource === 'auto';
          const nextAuto =
            (remind === 'open' || remind === 'cooling') && request.nextAutoRemindAt
              ? parisDateTime(request.nextAutoRemindAt)
              : null;
          return (
            <li key={request.id} className="rounded-xl border px-3 py-2.5">
              {/* The name and the pill share the top line, the sentence and the button the one below:
                  reading the first and last name is what the card is for, so nothing competes with it. */}
              <div className="flex items-start gap-[11px]">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-zinc-100 text-[12px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                  aria-hidden="true"
                >
                  {initials(technicianName(request))}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="flex min-w-0 flex-wrap items-center gap-1.5 break-words text-[13.5px] font-semibold">
                      {technicianName(request)}
                      {request.zone === 'secondary' && <SecondaryZonePill />}
                    </p>
                    <span
                      className={cn(
                        'inline-flex h-[23px] shrink-0 items-center gap-1.5 rounded-full pl-2 pr-[9px] text-xs font-medium',
                        statusTone(request.status)
                      )}
                    >
                      <span
                        className={cn('h-1.5 w-1.5 rounded-full', statusDot(request.status))}
                        aria-hidden="true"
                      />
                      {rowStatusLabel(t, request, remind)}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between gap-2">
                    <p className="flex min-w-0 flex-wrap items-center gap-1.5 text-[12.5px] text-muted-foreground">
                      {auto && (
                        <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                          {t('Auto')}
                        </span>
                      )}
                      {auto && request.lastReminderAt
                        ? `${t('Reminded {{count}} time', { count: request.reminderCount })} · ${t(
                            'last automatic reminder {{date}} at {{time}}',
                            parisDateTime(request.lastReminderAt)
                          )}`
                        : rowPhrase(t, request, acceptor)}
                    </p>
                    {/* On a pending request only: an answered one has nothing left to withdraw. The card
                        is titled « Techniciens sollicités », so the button says only what it undoes. */}
                    {request.status === 'pending' && (
                      <div className="flex shrink-0 gap-1.5">
                        {(remind === 'open' || remind === 'cooling') && (
                          <RemindButton
                            cooling={remind === 'cooling'}
                            remindingId={remindingId}
                            requestId={request.id}
                            onClick={() => onRemind(request)}
                          />
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          // 28 px drawn, 44 px hit area: the pseudo-element extends the target.
                          className="relative h-7 shrink-0 px-2.5 before:absolute before:-inset-y-2 before:inset-x-0 before:content-['']"
                          title={t('The request is withdrawn: the technician no longer sees it and is no longer reminded')}
                          onClick={() => onCancel(request)}
                        >
                          {t('Cancel')}
                        </Button>
                      </div>
                    )}
                  </div>
                  {hint && <p className={cn('mt-1 text-[12px]', hint.tone)}>{hint.text}</p>}
                  {nextAuto && (
                    <p className="mt-1 text-[12px] text-muted-foreground">
                      {t('Next automatic reminder: {{date}} at {{time}}', nextAuto)}
                    </p>
                  )}
                  {request.status === 'pending' && feeControl(request)}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {pendingCount > 0 && reminderSettings && (
        <p className="text-[11.5px] leading-relaxed text-muted-foreground/70">{reminderNote(t, reminderSettings)}</p>
      )}
      {/* R-072 — with nobody retained there is nothing to release, and the sentence says what to
          do instead rather than offering a button that would cancel nothing. */}
      {acceptor && (
        <div className="flex flex-col rounded-xl border px-3 py-2.5">
          <p className="flex flex-wrap items-center gap-1.5 text-[13.5px] font-semibold">
            {technicianName(acceptor)}
            {acceptor.zone === 'secondary' && <SecondaryZonePill />}
          </p>
          {feeControl(acceptor)}
        </div>
      )}
      {acceptor ? (
        <Button
          type="button"
          variant="outline"
          className="mt-1 min-h-[44px] gap-1.5 self-end text-[12.5px]"
          onClick={onRelease}
        >
          <UserMinus className="h-4 w-4" aria-hidden="true" />
          {t('Release the mission')}
        </Button>
      ) : (
        pendingCount === 0 && (
          <p className="text-[12px] text-muted-foreground">
            {t('No acceptor: nothing to release. Solicit other technicians below.')}
          </p>
        )
      )}
      {remindError ? (
        <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
          {apiErrorMessage(t, remindError)}
        </p>
      ) : null}
      {cancelError ? (
        <p className="text-[12.5px] text-rose-600 dark:text-rose-400" role="alert">
          {apiErrorMessage(t, cancelError)}
        </p>
      ) : null}
    </section>
  );
};
